import React, { useMemo } from "react";
import {
  GroupRole,
  MembershipRole,
  PermissionPickableWithDisabled,
  SystemRole,
} from "../../components/shared/DirectoryPicker/types";
import DirectoryPicker, { DirectoryPickerProps } from "./DirectoryPicker";
import { find, includes, omit } from "lodash";
import { GroupAdminEntry, GroupCategoryEntry } from "../directory/api";
import { Group } from "../../@types";

type RoleDirectoryPickerQuery = {
  include_membership_roles?: boolean;
  includeGroupCategories?: boolean;
  group_slug?: string;
  section?: string;
  per_page?: number;
  groups_only?: boolean;
};

type RoleDirectoryPickerProps = Omit<DirectoryPickerProps, "query"> & {
  query?: RoleDirectoryPickerQuery;
  includeAuthorRole?: boolean;
  // Fallback group descriptor for network admins administering a group they aren't a
  // member of: Preload.my_groups only contains the member's own groups, so without
  // this descriptor findGroupBySlug misses and the picker falls into systemRoles —
  // making the group_admin option unreachable. Ignored when the group resolves via
  // my_groups (member-admin back-compat).
  fallbackGroup?: { id: string; name: string };
};

function findGroupBySlug(slug: string) {
  return find(Preload.my_groups, { slug });
}

export const RoleDirectoryPicker = React.forwardRef(
  function RoleDirectoryPicker(
    props: RoleDirectoryPickerProps,
    // react-hook-form adds ref prop which we dont need but leads to warning unless we accept it with React.forwardRef
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _ref,
  ) {
    const groupSlug = props.query?.group_slug;
    const resolvedFromMyGroups = groupSlug
      ? findGroupBySlug(groupSlug)
      : undefined;

    // When groupSlug is set but my_groups misses (non-member network admin), synthesize
    // a minimal Group from the fallback descriptor. The `as unknown as Group` cast is
    // deliberate — Group has many required fields a non-member admin can't populate
    // without fetching; only `membership_roles: []` is load-bearing (downstream `.map`
    // would TypeError on undefined). useMemo gives the synthesized object a stable
    // identity so downstream memos don't invalidate every render.
    const fallbackGroupId = props.fallbackGroup?.id;
    const fallbackGroupName = props.fallbackGroup?.name;
    const group = useMemo<Group | undefined>(() => {
      if (resolvedFromMyGroups) return resolvedFromMyGroups;
      if (!(groupSlug && fallbackGroupId && fallbackGroupName))
        return undefined;

      return {
        id: fallbackGroupId,
        slug: groupSlug,
        name: fallbackGroupName,
        // Load-bearing stubs (see comment above): groupRoles' `find` tolerates
        // undefined; groupMembershipRoles' `.map` requires an array.
        membership_roles: [],
      } as unknown as Group;
    }, [resolvedFromMyGroups, groupSlug, fallbackGroupId, fallbackGroupName]);

    const extraOptions = useMemo(
      () =>
        async function extraOptions(
          inputValue: string,
        ): Promise<PermissionPickableWithDisabled[]> {
          let options: PermissionPickableWithDisabled[] = [];

          if (group) {
            options = options.concat(groupRoles(inputValue, group));
          } else {
            options = options.concat(
              systemRoles(inputValue, {
                includeAuthorRole: props.includeAuthorRole,
              }),
            );
          }
          if (props.query?.include_membership_roles)
            if (group) {
              options = options.concat(groupMembershipRoles(inputValue, group));
            } else {
              options = options.concat(networkMembershipRoles(inputValue));
            }

          if (props.query?.includeGroupCategories) {
            options = options.concat(groupCategories(inputValue));
          }

          return options;
        },
      [
        props.query?.includeGroupCategories,
        props.query?.include_membership_roles,
        group,
        props.includeAuthorRole,
      ],
    );
    const query = group?.id
      ? {
          ...omit(props.query, "group_slug", "section"),
          group_scope: group.id,
        }
      : props.query;

    return (
      <DirectoryPicker
        {...props}
        query={query}
        endpoint={group ? "/directory/members" : props.endpoint}
        multi
        extraOptions={extraOptions}
      />
    );
  },
);

/**
 * UI element analogous to the selection of memberships in the Appointment-Create form (invitations) to assign the following users/roles to an action:
 *
 *  - Network administrators
 *  - Groups (all members)
 *  - Groups (administrators only)
 *  - Groups (membership role)
 *  - Network-wide memberships role
 *  - Individual memberships
 */
type PermissionPickerProps = Omit<
  RoleDirectoryPickerProps,
  "query" | "placeholder" | "multi" | "extraOptions" | "onChange"
> & {
  query?: Pick<
    RoleDirectoryPickerQuery,
    | "group_slug"
    | "section"
    | "per_page"
    | "includeGroupCategories"
    | "groups_only"
  >;
  // Simplified onChange that just receives the array (PermissionPicker always uses multi)
  onChange?: (value: PermissionPickableWithDisabled[]) => void;
};

const PermissionPicker = React.forwardRef(function PermissionPicker(
  props: PermissionPickerProps,
  // react-hook-form adds ref prop which we dont need but leads to warning unless we accept it with React.forwardRef
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _ref,
) {
  return (
    <RoleDirectoryPicker
      {...props}
      onChange={
        props.onChange
          ? (newValue) =>
              props.onChange?.(newValue as PermissionPickableWithDisabled[])
          : undefined
      }
      placeholder={I18n.t("js.permission_picker.placeholder")}
      query={{ include_membership_roles: true, ...props.query }}
      includeAuthorRole={props.includeAuthorRole}
    />
  );
});

export default PermissionPicker;

function systemRoles(
  inputValue: string,
  options?: { includeAuthorRole?: boolean },
): PermissionPickableWithDisabled[] {
  const roles: SystemRole[] = [
    {
      type: "system_role",
      id: "network_admin",
      name: I18n.t("js.permission_picker.system_roles.network_admin"),
    },
    {
      type: "system_role",
      id: "network_member",
      name: I18n.t("js.permission_picker.system_roles.network_member"),
    },
  ];

  // Only show guest_user if the network allows it
  if (Preload.current_network.config.guest_users?.allowed) {
    roles.push({
      type: "system_role",
      id: "guest_user",
      name: I18n.t("js.permission_picker.system_roles.guest_user"),
    });
  }

  // Only show author role if explicitly enabled
  if (options?.includeAuthorRole) {
    roles.push({
      type: "system_role",
      id: "author",
      name: I18n.t("js.permission_picker.system_roles.author"),
    });
  }

  return filterByInputValue(roles, inputValue);
}

function groupRoles(
  inputValue: string,
  group: Group,
): PermissionPickableWithDisabled[] {
  const groupCategory = find(Preload.current_network.group_categories, {
    id: group.category_id,
  });

  const roles: (GroupRole | GroupAdminEntry)[] = [
    {
      type: "group",
      id: group.id,
      name: group.name,
      category: groupCategory,
    },
    {
      type: "group_admin",
      id: group.id,
      name: I18n.t("js.permission_picker.group_roles.group_admin", {
        group_name: group.name,
      }),
    },
  ];
  return filterByInputValue(roles, inputValue);
}

export function networkMembershipRoles(
  inputValue: string,
): PermissionPickableWithDisabled[] {
  const membershipRoles: MembershipRole[] = (
    Preload.current_network.membership_roles ?? []
  ).map((r) => ({
    ...r,
    type: "membership_role",
  }));

  return filterByInputValue(membershipRoles, inputValue);
}

export function groupMembershipRoles(
  inputValue: string,
  group: Group,
): PermissionPickableWithDisabled[] {
  const membershipRoles: MembershipRole[] = group.membership_roles.map((r) => ({
    ...r,
    type: "membership_role",
    parent_type: "Group",
    parent_id: group.id,
  }));

  return filterByInputValue(membershipRoles, inputValue);
}

function groupCategories(inputValue: string): PermissionPickableWithDisabled[] {
  const groupCategories: GroupCategoryEntry[] =
    Preload.current_network.group_categories.map((gc) => ({
      ...gc,
      type: "group_category",
    }));

  return filterByInputValue(groupCategories, inputValue);
}

function filterByInputValue<T extends { name: string }>(
  items: T[],
  inputValue: string,
): T[] {
  return items.filter((r) =>
    includes(r.name.toLowerCase(), inputValue.toLowerCase()),
  );
}
